import React, { useState, useEffect } from 'react';
import InputField from './InputField';
import ChatWindow from './ChatWindow';
import {
  BedrockRuntimeClient,
  InvokeModelCommand,
} from '@aws-sdk/client-bedrock-runtime';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';

const REGION = 'us-west-2';
const IDENTITY_POOL_ID = 'us-west-2:542b2412-de22-4a12-b547-0aa62eef3b6d';
const MODEL_ID = 'anthropic.claude-instant-v1';

export default function ChatbotApp({ tokens }) {
  const [messages, setMessages] = useState([]);

  const [userTokens, setUserTokens] = useState(null);

  // Save tokens when passed in
  useEffect(() => {
    if (tokens?.idToken) {
      localStorage.setItem('idToken', tokens.idToken);
      setUserTokens(tokens.idToken);
    } else {
      const saved = localStorage.getItem('idToken');
      if (saved) setUserTokens(saved);
    }
    }, [tokens]);

  const handleLogout = () => {
    localStorage.removeItem('idToken');
    window.location.reload(); // or navigate to login page if using router
  };

  const bedrockClient = new BedrockRuntimeClient({
    region: REGION,
    credentials: fromCognitoIdentityPool({
      identityPoolId: IDENTITY_POOL_ID,
      clientConfig: { region: REGION },
      logins: {
        [`cognito-idp.${REGION}.amazonaws.com/us-west-2_GIeeMdQDA`]: tokens.idToken,
      },
    }),
  });

  const addMessage = (msg, isUser = null) => {
    if (typeof msg === 'string') {
      setMessages((prev) => [...prev, { type: 'text', text: msg, isUser }]);
    } else {
      setMessages((prev) => [...prev, msg]);
    }
  };

  const formatMessagesAsPrompt = (messages, userInput) => {
    const MAX_HISTORY = 10;
    const trimmed = messages.slice(-MAX_HISTORY);

    let prompt ="Human: Always answer in a kid friendly way.Make it cheerful and not plain text."
    trimmed.forEach((msg) => {
      if (msg.text) {
        prompt += msg.isUser ? `\n\nHuman: ${msg.text}` : `\n\nAssistant: ${msg.text}`;
      }
    });

    prompt += `\n\nHuman: ${userInput}\n\nAssistant:`;
    return prompt;
  };

  const reviewAnswer = async (answer) => {
    const reviewPrompt = `\n\nHuman: You are checking if the response below is safe and easy to understand for a 7 to 10 year old child.
Reply only with:
- "Kid friendly ✅" if it's safe.
- "Too grown-up ❌" if it talks about adult topics like money, politics, relationships, etc.

Here is the response:
"""
${answer}
"""
\n\nAssistant:`;

    const command = new InvokeModelCommand({
      modelId: MODEL_ID,
      contentType: 'application/json',
      accept: 'application/json',
      body: JSON.stringify({
        prompt: reviewPrompt,
        max_tokens_to_sample: 30,
        temperature: 0.3,
      }),
    });

    const response = await bedrockClient.send(command);
    const responseBody = await response.body.transformToString();
    const parsed = JSON.parse(responseBody);
    return parsed.completion?.includes('Kid friendly ✅');
  };

  const rewriteAnswer = async (originalAnswer) => {
    const rewritePrompt = `\n\nHuman: This response is too grown-up. Please rewrite it in a fun, cheerful, and easy-to-understand way for a child aged 7 to 10.
Make it safe and enjoyable — maybe add a fun animal fact or a joke if needed.

Grown-up response:
"""
${originalAnswer}
"""

Kid-friendly version:
\n\nAssistant:`;

    const command = new InvokeModelCommand({
      modelId: MODEL_ID,
      contentType: 'application/json',
      accept: 'application/json',
      body: JSON.stringify({
        prompt: rewritePrompt,
        max_tokens_to_sample: 300,
        temperature: 0.5,
      }),
    });

    const response = await bedrockClient.send(command);
    const responseBody = await response.body.transformToString();
    const parsed = JSON.parse(responseBody);
    return parsed.completion?.trim() || 'Here’s something fun instead!';
  };

  const sendUserMessage = async (message) => {
    addMessage(message, true);

    const prompt = formatMessagesAsPrompt(messages, message);

    const command = new InvokeModelCommand({
      modelId: MODEL_ID,
      contentType: 'application/json',
      accept: 'application/json',
      body: JSON.stringify({
        prompt,
        max_tokens_to_sample: 300,
        temperature: 0.5,
      }),
    });

    try {
      const response = await bedrockClient.send(command);
      const responseBody = await response.body.transformToString();
      const parsed = JSON.parse(responseBody);
      const answer = parsed.completion?.trim() || 'Sorry, I had no response.';

      const isKidFriendly = await reviewAnswer(answer);

      if (isKidFriendly) {
        addMessage(answer, false);
      } else {
        const safeAnswer = await rewriteAnswer(answer);
        addMessage(safeAnswer, false);
      }
    } catch (err) {
      console.error('Bedrock error:', err);
      addMessage('Oops! Something went wrong talking to the cloud brain.', false);
    }
  };

  useEffect(() => {
    setMessages((prev) => {
      if (prev.some((msg) => msg.text === 'Welcome! How can I assist you today?')) {
        return prev;
      }
      return [
        ...prev,
        { type: 'text', text: 'Welcome! How can I assist you today?', isUser: false },
      ];
    });
  }, []);

  if (!userTokens || !bedrockClient) {
    return <div>Loading your session...</div>;
  }



  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px', textAlign: 'right' }}>
        <button onClick={handleLogout} style={{
          backgroundColor: '#ff4d4f',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '8px',
          cursor: 'pointer'
        }}>
          Logout
        </button>
      </div>
      <ChatWindow messages={messages} />
      <InputField onSendMessage={sendUserMessage} />
    </div>
  );
}

